import React, { useState } from 'react';
import { Home, DollarSign, Calculator, AlertCircle, TrendingUp } from 'lucide-react';

function App() {
  const [mode, setMode] = useState('analyze'); // 'analyze' or 'search'
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [propertyData, setPropertyData] = useState(null);
  const [error, setError] = useState('');
  
  // User inputs
  const [downPayment, setDownPayment] = useState(8000);
  const [loanType, setLoanType] = useState('conventional'); // 'conventional', 'fha', 'custom'
  const [interestRate, setInterestRate] = useState(7.0);
  const [customDownPercent, setCustomDownPercent] = useState(3);

  // For search mode
  const [searchCriteria, setSearchCriteria] = useState({
    bedrooms: 3,
    bathrooms: 2,
    maxMonthly: 2800,
    hasGarage: true,
    hasYard: true,
    area: 'Kyle, TX'
  });

  const calculateMonthlyPayment = (principal, rate, years = 30) => {
    const monthlyRate = rate / 100 / 12;
    const numPayments = years * 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                    (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  const analyzeProperty = () => {
    setLoading(true);
    setError('');
    
    // Mock data for now - we'll integrate real APIs later
    // This would normally fetch from Zillow API or scrape the listing
    setTimeout(() => {
      const mockData = {
        address: address,
        price: 260000,
        bedrooms: 3,
        bathrooms: 2,
        sqft: 2061,
        yearBuilt: 2005,
        propertyTax: 7311, // annual
        hoaFee: 333, // annual
        lotSize: 7936,
        estimatedInsurance: 1800 // annual estimate
      };
      setPropertyData(mockData);
      setLoading(false);
    }, 1000);
  };

  const calculateTotalCosts = () => {
    if (!propertyData) return null;

    const price = propertyData.price;
    let downPaymentPercent;
    let downPaymentAmount;
    
    if (loanType === 'fha') {
      downPaymentPercent = 3.5;
    } else if (loanType === 'conventional') {
      downPaymentPercent = 3;
    } else {
      downPaymentPercent = customDownPercent;
    }

    // If user entered dollar amount, use that; otherwise calculate from percent
    downPaymentAmount = downPayment > 0 ? downPayment : (price * downPaymentPercent / 100);
    const loanAmount = price - downPaymentAmount;
    const actualDownPercent = (downPaymentAmount / price) * 100;

    // Calculate monthly payments
    const principalInterest = calculateMonthlyPayment(loanAmount, interestRate);
    const monthlyTax = propertyData.propertyTax / 12;
    const monthlyInsurance = propertyData.estimatedInsurance / 12;
    const monthlyHOA = propertyData.hoaFee / 12;
    
    // PMI calculation (if less than 20% down)
    let monthlyPMI = 0;
    if (actualDownPercent < 20) {
      if (loanType === 'fha') {
        // FHA has both upfront and monthly MIP
        monthlyPMI = loanAmount * 0.0085 / 12; // 0.85% annually for FHA
      } else {
        // Conventional PMI typically 0.5-1% of loan annually
        monthlyPMI = loanAmount * 0.007 / 12; // 0.7% annually
      }
    }

    const totalMonthly = principalInterest + monthlyTax + monthlyInsurance + monthlyHOA + monthlyPMI;

    // Closing costs estimate (typically 2-5% of purchase price)
    const estimatedClosingCosts = price * 0.03;
    const totalCashNeeded = downPaymentAmount + estimatedClosingCosts;

    // Income needed (using 28% front-end ratio)
    const monthlyIncomeNeeded = totalMonthly / 0.28;
    const annualIncomeNeeded = monthlyIncomeNeeded * 12;

    return {
      downPaymentAmount,
      downPaymentPercent: actualDownPercent,
      loanAmount,
      principalInterest,
      monthlyTax,
      monthlyInsurance,
      monthlyHOA,
      monthlyPMI,
      totalMonthly,
      estimatedClosingCosts,
      totalCashNeeded,
      annualIncomeNeeded
    };
  };

  const costs = propertyData ? calculateTotalCosts() : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Home className="w-10 h-10 text-indigo-600" />
            <h1 className="text-4xl font-bold text-gray-800">AvRi Home Search</h1>
          </div>
          <p className="text-gray-600 text-lg">
            See the REAL cost of homeownership - no BS, just honest numbers
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-4 mb-8 justify-center">
          <button
            onClick={() => setMode('analyze')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              mode === 'analyze' 
                ? 'bg-indigo-600 text-white shadow-lg' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Calculator className="w-5 h-5 inline mr-2" />
            Analyze a House
          </button>
          <button
            onClick={() => setMode('search')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              mode === 'search' 
                ? 'bg-indigo-600 text-white shadow-lg' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-5 h-5 inline mr-2" />
            Find Affordable Houses
          </button>
        </div>

        {/* Analyze Mode */}
        {mode === 'analyze' && (
          <div className="space-y-6">
            {/* Input Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Enter Property Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Address or Zillow URL
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="224 Maplewood S, Kyle, TX or paste Zillow link"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Down Payment ($)
                    </label>
                    <input
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(Number(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loan Type
                    </label>
                    <select
                      value={loanType}
                      onChange={(e) => setLoanType(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="conventional">Conventional (3% down)</option>
                      <option value="fha">FHA (3.5% down)</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interest Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={interestRate}
                      onChange={(e) => setInterestRate(Number(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <button
                  onClick={analyzeProperty}
                  disabled={loading || !address}
                  className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Analyzing...' : 'Calculate Real Cost'}
                </button>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <p className="text-red-800">{error}</p>
                </div>
              )}
            </div>

            {/* Results Section */}
            {propertyData && costs && (
              <div className="space-y-6">
                {/* Property Overview */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">Property Overview</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">List Price</p>
                      <p className="text-xl font-bold text-gray-800">
                        ${propertyData.price.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Beds / Baths</p>
                      <p className="text-xl font-bold text-gray-800">
                        {propertyData.bedrooms} / {propertyData.bathrooms}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Square Feet</p>
                      <p className="text-xl font-bold text-gray-800">
                        {propertyData.sqft.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Year Built</p>
                      <p className="text-xl font-bold text-gray-800">
                        {propertyData.yearBuilt}
                      </p>
                    </div>
                  </div>
                </div>

                {/* The Real Monthly Cost */}
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                  <h2 className="text-2xl font-semibold mb-2">Your TRUE Monthly Payment</h2>
                  <p className="text-5xl font-bold mb-4">
                    ${Math.round(costs.totalMonthly).toLocaleString()}
                    <span className="text-xl font-normal">/month</span>
                  </p>
                  <p className="text-indigo-100">
                    This is what you'll ACTUALLY pay each month, including all the stuff Zillow hides
                  </p>
                </div>

                {/* Monthly Breakdown */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Monthly Payment Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Mortgage (Principal & Interest)</span>
                      <span className="font-semibold">${Math.round(costs.principalInterest).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Property Taxes</span>
                      <span className="font-semibold">${Math.round(costs.monthlyTax).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Homeowners Insurance</span>
                      <span className="font-semibold">${Math.round(costs.monthlyInsurance).toLocaleString()}</span>
                    </div>
                    {costs.monthlyHOA > 0 && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-gray-700">HOA Fees</span>
                        <span className="font-semibold">${Math.round(costs.monthlyHOA).toLocaleString()}</span>
                      </div>
                    )}
                    {costs.monthlyPMI > 0 && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-gray-700">PMI (Mortgage Insurance)</span>
                        <span className="font-semibold text-orange-600">
                          ${Math.round(costs.monthlyPMI).toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-3 bg-indigo-50 px-4 -mx-4 rounded">
                      <span className="text-gray-900 font-semibold text-lg">Total Monthly</span>
                      <span className="font-bold text-xl text-indigo-600">
                        ${Math.round(costs.totalMonthly).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cash Needed */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Cash You Need to Close</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">
                        Down Payment ({costs.downPaymentPercent.toFixed(1)}%)
                      </span>
                      <span className="font-semibold">
                        ${Math.round(costs.downPaymentAmount).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Estimated Closing Costs (3%)</span>
                      <span className="font-semibold">
                        ${Math.round(costs.estimatedClosingCosts).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-amber-50 px-4 -mx-4 rounded">
                      <span className="text-gray-900 font-semibold text-lg">Total Cash Needed</span>
                      <span className="font-bold text-xl text-amber-600">
                        ${Math.round(costs.totalCashNeeded).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {downPayment < costs.totalCashNeeded && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-amber-800">
                        <strong>Heads up:</strong> You'll need ${Math.round(costs.totalCashNeeded - downPayment).toLocaleString()} more 
                        in savings to cover closing costs and down payment.
                      </p>
                    </div>
                  )}
                </div>

                {/* Affordability Check */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    <DollarSign className="w-6 h-6 inline mr-2 text-green-600" />
                    Can You Afford This?
                  </h3>
                  <p className="text-gray-700 mb-3">
                    Lenders typically want your housing payment to be no more than 28% of your gross monthly income.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-gray-800">
                      <strong>Recommended annual income:</strong>{' '}
                      <span className="text-2xl font-bold text-blue-600">
                        ${Math.round(costs.annualIncomeNeeded).toLocaleString()}
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      (That's about ${Math.round(costs.annualIncomeNeeded / 12).toLocaleString()}/month gross income)
                    </p>
                  </div>
                </div>

                {/* Home Buying Jargon Explained */}
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg shadow-md p-6 border-l-4 border-blue-400">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    📚 Home Buying Jargon Explained
                  </h3>
                  <div className="space-y-3 text-gray-700">
                    <div>
                      <strong className="text-blue-600">PMI (Private Mortgage Insurance):</strong> Extra insurance you pay when putting down less than 20%. Protects the lender, not you. 
                      {loanType === 'fha' 
                        ? ' FHA calls it MIP and it stays for the life of the loan unless you refinance.' 
                        : ' Conventional PMI drops off automatically at 78% loan-to-value.'}
                    </div>
                    <div>
                      <strong className="text-blue-600">Principal & Interest:</strong> Principal is the actual loan amount you're paying back. Interest is what the bank charges you to borrow that money.
                    </div>
                    <div>
                      <strong className="text-blue-600">Closing Costs:</strong> Fees for processing your loan, title search, appraisal, inspections, etc. Typically 2-5% of purchase price. Not the same as down payment.
                    </div>
                    <div>
                      <strong className="text-blue-600">Escrow:</strong> Your lender collects money monthly for property taxes and insurance, then pays those bills for you when they're due.
                    </div>
                    <div>
                      <strong className="text-blue-600">Loan-to-Value (LTV):</strong> The percentage of the home's value you're borrowing. 95% LTV means 5% down payment.
                    </div>
                    <div>
                      <strong className="text-blue-600">DTI (Debt-to-Income):</strong> All your monthly debt payments divided by gross monthly income. Lenders want this below 43-50%.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Mode - Coming Soon */}
        {mode === 'search' && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <TrendingUp className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Search Feature Coming Soon!
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're building a tool to search for houses you can actually afford based on your budget,
              not just list price. It'll show you the TRUE monthly cost for every listing. Stay tuned!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
